import React, { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { FiEye, FiEyeOff } from "react-icons/fi";
import adminService from "../api/services/adminService";
import Loading from "../components/Loading";
import toast from "react-hot-toast";
import {
  PHONE_DIAL_CODES as phoneCodes,
  clampNationalDigitsForDial,
  getNationalLengthLimits,
  parseContactForDialSelect,
} from "../data/phoneDialCodes";

const AdminPanelUserModal = ({
  isOpen,
  onClose,
  editingUser,
  trigger,
  setTrigger,
}) => {
  const style =
    "text-left tracking-wide text-grey-darker text-[0.7rem] font-[600] mb-[3px] ";
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneCode, setPhoneCode] = useState("+92"); // Default to Pakistan

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    contactNo: "",
    role: "Admin",
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    contactNo: false,
    role: false,
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const result = await adminService.getAllRoles();
        setRoles(result?.roles || []);
      } catch (error) {
        console.error("Error fetching roles:", error);
        toast.error("Failed to fetch roles");
      } finally {
        setRolesLoading(false);
      }
    };

    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (editingUser) {
        // Edit mode - populate with existing data
        const contactNo = editingUser.contactNo || "";
        const { dial, national } = parseContactForDialSelect(contactNo);
        setPhoneCode(dial);
        setData({
          firstName: editingUser.firstName || "",
          lastName: editingUser.lastName || "",
          email: editingUser.email || "",
          password: "", // Don't pre-fill password
          contactNo: national,
          role: editingUser.roleName || "Admin",
        });
      } else {
        // Create mode - reset form
        setPhoneCode("+92");
        setData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          contactNo: "",
          role: "Admin",
        });
      }
      setShowPassword(false);
      setErrors({
        firstName: false,
        lastName: false,
        email: false,
        password: false,
        contactNo: false,
        role: false,
      });
    }
  }, [isOpen, editingUser]);

  const handleChange = (e) => {
    if (e.target.name === "contactNo") {
      const value = clampNationalDigitsForDial(phoneCode, e.target.value);
      setData((d) => ({ ...d, [e.target.name]: value }));
      if (value.length > 0) {
        setErrors((prevState) => ({ ...prevState, [e.target.name]: false }));
      }
    } else {
      setData((d) => ({ ...d, [e.target.name]: e.target.value }));
      if (e.target.value.length > 0) {
        setErrors((prevState) => ({ ...prevState, [e.target.name]: false }));
      }
    }
  };

  const handlePhoneCodeChange = (e) => {
    const next = e.target.value;
    setPhoneCode(next);
    // Clamp existing digits to new max
    setData((d) => ({ ...d, contactNo: clampNationalDigitsForDial(next, d.contactNo) }));
    setErrors((prevState) => ({ ...prevState, contactNo: false }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    const { min, max } = getNationalLengthLimits(phoneCode);
    return digits.length >= min && digits.length <= max;
  };

  const submit = async () => {
    let hasError = false;
    // Password is only required for new users (not when editing)
    const requiredFields = editingUser
      ? ["firstName", "lastName", "email", "contactNo", "role"]
      : ["firstName", "lastName", "email", "password", "contactNo", "role"];

    requiredFields.forEach((field) => {
      if (!data[field] || data[field].length === 0) {
        setErrors((prevState) => ({ ...prevState, [field]: true }));
        hasError = true;
      }
    });

    if (!validateEmail(data.email)) {
      setErrors((prevState) => ({ ...prevState, email: true }));
      hasError = true;
    }

    // Password validation only for new users (not when editing)
    if (!editingUser && data.password && data.password.length < 6) {
      setErrors((prevState) => ({ ...prevState, password: true }));
      hasError = true;
      toast.error("Password must be at least 6 characters long");
    }
    // For edit mode, password is optional
    if (
      editingUser &&
      data.password &&
      data.password.length > 0 &&
      data.password.length < 6
    ) {
      setErrors((prevState) => ({ ...prevState, password: true }));
      hasError = true;
      toast.error("Password must be at least 6 characters long");
    }

    if (!validatePhone(data.contactNo)) {
      setErrors((prevState) => ({ ...prevState, contactNo: true }));
      hasError = true;
      const { min, max } = getNationalLengthLimits(phoneCode);
      const range =
        min === max ? `exactly ${min} digits` : `${min}–${max} digits`;
      toast.error(`Please enter a valid phone number (${range} after the code)`);
    }

    if (hasError) {
      return;
    }

    try {
      setLoading(true);
      // Combine phone code with contact number
      const fullContactNo = `${phoneCode}${data.contactNo}`;

      if (editingUser) {
        // Update mode
        const updateData = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          contactNo: fullContactNo,
          role: data.role,
        };
        // Only include password if it's provided
        if (data.password && data.password.length > 0) {
          updateData.password = data.password;
        }
        await adminService.updateAdminPanelUser(editingUser.id, updateData);
        toast.success("Admin panel user updated successfully!");
      } else {
        // Create mode
        const createData = {
          ...data,
          contactNo: fullContactNo,
        };
        await adminService.createAdminPanelUser(createData);
        toast.success("Admin panel user created successfully!");
      }
      setTrigger(!trigger);
      onClose();
    } catch (error) {
      console.error(
        `Error ${editingUser ? "updating" : "creating"} admin panel user:`,
        error
      );
      toast.error(
        error?.message ||
          `Failed to ${editingUser ? "update" : "create"} admin panel user`
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-2 md:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-1 md:p-4 flex justify-between items-center">
          <h2 className="text-md md:text-xl font-bold text-gray-800">
            {editingUser ? "Edit Admin Panel User" : "Create Admin Panel User"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700">
            <IoMdClose className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {rolesLoading ? (
            <Loading />
          ) : (
            <>
              <div className="mb-4">
                <label className={style}>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={data.firstName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.firstName ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    First name is required
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={data.lastName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.lastName ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    Last name is required
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={data.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter email"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    Valid email is required
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Password {!editingUser && "*"}</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={data.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-xs md:text-sm pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder={
                      editingUser
                        ? "Leave blank to keep current password"
                        : "Enter password (min 6 characters)"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none">
                    {showPassword ? (
                      <FiEyeOff size={20} />
                    ) : (
                      <FiEye size={20} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">
                    Password must be at least 6 characters
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Contact Number *</label>
                <div className="flex gap-2">
                  <select
                    value={phoneCode}
                    onChange={handlePhoneCodeChange}
                    className="px-1 md:px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white min-w-[120px]">
                    {phoneCodes.map((pc) => (
                      <option key={pc.code} value={pc.code}>
                        {pc.code} ({pc.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="contactNo"
                    value={data.contactNo}
                    onChange={handleChange}
                    className={`flex-1 px-1 md:px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.contactNo ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter phone digits"
                    maxLength={10}
                  />
                </div>
                  {errors.contactNo && (
                    <p className="text-red-500 text-xs mt-1">
                      Please enter a valid phone number for the selected country code.
                    </p>
                  )}
                {data.contactNo.length > 0 && data.contactNo.length < 10 && (
                  <p className="text-gray-500 text-xs mt-1">
                    {10 - data.contactNo.length} digits remaining
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label className={style}>Role *</label>
                <select
                  name="role"
                  value={data.role}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.role ? "border-red-500" : "border-gray-300"
                  }`}>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="text-red-500 text-xs mt-1">Role is required</p>
                )}
              </div>

              <div className="flex gap-3 text-xs md:text-sm justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={loading}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading
                    ? editingUser
                      ? "Updating..."
                      : "Creating..."
                    : editingUser
                    ? "Update User"
                    : "Create User"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanelUserModal;
